import { FC, useCallback, useEffect, useState } from 'react';
import { Space } from 'antd';
import { useFormik } from 'formik';
import { useNavigate, useParams } from 'react-router-dom';
import Button from 'components/new-ui/base/button/Button';
import PageHeader from 'components/new-ui/base/page-header/PageHeader';
import FormInput from 'components/new-ui/composed/formInput/FormInput';
import FormSelect from 'components/new-ui/composed/formSelect/FormSelect';
import Notification from 'components/ui/base/notification/Notification';
import useBreadcrumb from 'hooks/useBreadcrumb';
import useCommodityConditions from 'hooks/useCommodityConditions';
import { CommodityCondition } from 'store/commodity-conditions-center/commodityCondition';
import useCommodityConditionCodelists from './hooks/useCommodityConditionCodelists';
import { COMMODITY_CONDITIONS_LIST_PATH } from './utils/commodityConditionPath';
import toCommodityConditionRequest from './utils/toCommodityConditionRequest';
import { commodityConditionValidationSchema } from './validationSchema';
import toCommodityConditionFormValues, { CommodityConditionFormValues } from './utils/toCommodityConditionFormValues';

const AddEditCommodityCondition: FC = () => {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const { setBreadcrumbRoutes } = useBreadcrumb();
    const { getCondition, createCondition, editCondition } = useCommodityConditions();
    const { countryOptions, conditionCodeOptions, isLoading: isLoadingCodelists } = useCommodityConditionCodelists();
    const [loadedCondition, setLoadedCondition] = useState<CommodityCondition>();

    const loadCondition = useCallback(() => {
        if (!id) return;

        getCondition(id)
            .then(setLoadedCondition)
            .catch(() => {
                Notification({
                    type: 'error',
                    messageTitle: 'Failed to load commodity condition',
                    description: 'Please try again later',
                    placement: 'top',
                });
                navigate(COMMODITY_CONDITIONS_LIST_PATH, { replace: true });
            });
    }, [id, getCondition, navigate]);

    useEffect(() => {
        loadCondition();
    }, [loadCondition]);

    useEffect(() => {
        setBreadcrumbRoutes([
            { breadcrumbName: 'Home', path: '/home' },
            { breadcrumbName: 'Commodity Conditions Center', path: '../commodity-conditions-center' },
            { breadcrumbName: id ? loadedCondition?.commodityCode ?? '' : 'New', path: '' },
        ]);
    }, [setBreadcrumbRoutes, id, loadedCondition]);

    const formik = useFormik<CommodityConditionFormValues>({
        initialValues: toCommodityConditionFormValues(loadedCondition),
        enableReinitialize: true,
        validationSchema: commodityConditionValidationSchema,
        onSubmit: async (values) => {
            if (id && !loadedCondition) return;

            const request = toCommodityConditionRequest(values);
            // A stale version reloads the latest one, so the user can review it before saving again
            const saved = loadedCondition
                ? await editCondition(loadedCondition.id, loadedCondition.version, request, loadCondition)
                : await createCondition(request);

            if (saved) navigate(COMMODITY_CONDITIONS_LIST_PATH, { replace: true });
        },
    });

    const getFieldProps = (field: keyof CommodityConditionFormValues) => ({
        fieldProps: formik.getFieldProps(field),
        fieldMeta: formik.getFieldMeta(field),
        fieldHelper: formik.getFieldHelpers(field),
    });

    const title = id ? `Commodity Condition ${loadedCondition?.commodityCode ?? ''}` : 'New Commodity Condition';

    return (
        <Space direction="vertical" size={24} style={{ width: '100%' }}>
            <PageHeader title={title} showBackButton onBackButtonClick={() => navigate(-1)} />
            <form onSubmit={formik.handleSubmit} style={{ maxWidth: 640 }}>
                <Space direction="vertical" size={16} style={{ width: '100%' }}>
                    <FormInput
                        label="Commodity Code"
                        required
                        condensed
                        placeholder="8 digits"
                        maxLength={8}
                        {...getFieldProps('commodityCode')}
                    />
                    <FormInput
                        label="TARIC Code"
                        required
                        condensed
                        placeholder="2 digits"
                        maxLength={2}
                        {...getFieldProps('taricCode')}
                    />
                    <FormSelect
                        label="Country of Origin"
                        required
                        placeholder="Select country of origin"
                        selectOptions={countryOptions}
                        loading={isLoadingCodelists}
                        {...getFieldProps('countryOrigin')}
                    />
                    <FormSelect
                        label="Condition Codes"
                        mode="multiple"
                        optionLabelProp="value"
                        placeholder="Select condition codes"
                        selectOptions={conditionCodeOptions}
                        loading={isLoadingCodelists}
                        {...getFieldProps('conditionCodes')}
                    />
                    <Space size={8} style={{ width: '100%', justifyContent: 'flex-end' }}>
                        <Button onClick={() => navigate(COMMODITY_CONDITIONS_LIST_PATH)}>Cancel</Button>
                        <Button
                            type="primary"
                            htmlType="submit"
                            loading={formik.isSubmitting}
                            disabled={(Boolean(id) && !loadedCondition) || !formik.dirty}
                        >
                            Save
                        </Button>
                    </Space>
                </Space>
            </form>
        </Space>
    );
};

export default AddEditCommodityCondition;
